import type { LegacyTableColumn } from "@wayflyer/flyui-dataviz";
import { useCallback, useState } from "react";

// Matches the key/format previously written by LegacyTable's column selector so
// users keep their saved column preferences after the migration.
const STORAGE_SUFFIX = "selectedColumns";

type StoredSelection = Record<string, boolean>;

const readStoredSelection = (storageKey: string): StoredSelection | null => {
  if (!storageKey || typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as StoredSelection) : null;
  } catch {
    return null;
  }
};

const writeStoredSelection = (storageKey: string, selection: StoredSelection): void => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(selection));
  } catch {
    // Ignore write failures (e.g. storage disabled / quota exceeded).
  }
};

const isToggleable = (col: LegacyTableColumn) => col.allowSelection !== false;

type UseColumnVisibilityStorageArgs = {
  columns: LegacyTableColumn[];
  storagePath?: string;
  enabled: boolean;
};

type UseColumnVisibilityStorageResult = {
  initialHidden: string[];
  onHiddenColumnsChange: (hiddenColumns: Array<string | number>) => void;
};

/**
 * Derives the initially hidden columns from a column's `selectedByDefault` flag
 * and any persisted user selection, and persists subsequent visibility changes
 * back to localStorage in the legacy format.
 */
export const useColumnVisibilityStorage = ({
  columns,
  storagePath,
  enabled,
}: UseColumnVisibilityStorageArgs): UseColumnVisibilityStorageResult => {
  const storageKey = enabled && storagePath ? `${storagePath}${STORAGE_SUFFIX}` : "";

  const [initialHidden] = useState<string[]>(() => {
    if (!enabled) {
      return [];
    }
    const hidden = new Set(columns.filter((col) => col.selectedByDefault === false).map((col) => String(col.id)));
    const stored = readStoredSelection(storageKey);
    if (stored) {
      columns.forEach((col) => {
        if (!isToggleable(col)) {
          return;
        }
        const selected = stored[String(col.id)];
        if (selected === false) {
          hidden.add(String(col.id));
        } else if (selected === true) {
          hidden.delete(String(col.id));
        }
      });
    }
    return [...hidden];
  });

  // DataTable owns the live column-visibility state; this hook only seeds the
  // initial hidden set and persists subsequent changes, so it keeps no state of
  // its own (avoiding redundant re-renders of the table wrapper).
  const onHiddenColumnsChange = useCallback(
    (nextHidden: Array<string | number>) => {
      if (!storageKey) {
        return;
      }
      const hidden = new Set(nextHidden.map(String));
      // Merge into any existing stored selection rather than replacing it, so
      // preferences for columns absent from the current set (e.g. metric columns
      // gated by metadata) are preserved instead of dropped. The read path above
      // already tolerates a superset of keys, so the two stay symmetric.
      const selection: StoredSelection = { ...(readStoredSelection(storageKey) ?? {}) };
      columns.forEach((col) => {
        if (!isToggleable(col)) {
          return;
        }
        selection[String(col.id)] = !hidden.has(String(col.id));
      });
      writeStoredSelection(storageKey, selection);
    },
    [columns, storageKey]
  );

  return { initialHidden, onHiddenColumnsChange };
};
